/* eslint-disable react-refresh/only-export-components */

import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { toast } from 'sonner';
import type { IMaterialMapping } from '@/api/constants-mapping/types';
import type { FilterDef } from '@/components/shared/DataTable/types';
import { TextValue, UpdatedAtValue } from '@/components/shared/ValueRenderers';
import { EmptyState } from '@/components/ui/empty-state';
import { useTable } from '@/hooks/useTable';
import { useTranslator } from '@/hooks/useTranslator';
import { DeleteConfirmDialog } from '../../../-components/DeleteConfirmDialog';
import { MappingTable } from '../../../-components/MappingTable';
import { MaterialCreateDialog } from '../../../-components/MaterialCreateDialog';
import {
  materialMappingKeys,
  useCreateMaterialMappings,
  useDeleteMaterialMappings,
  useMaterialMappings,
  useMaterialsDropdown,
  usePatchMaterialMapping,
} from '../../../-hooks/useMaterialMappings';

export const Route = createFileRoute(
  '/(client)/constants-mapping/(pages)/(list)/materials/',
)({
  component: MaterialsPage,
});

function MaterialsPage() {
  const { getTranslation: t } = useTranslator({ page: 'constantsMapping' });
  const queryClient = useQueryClient();
  const table = useTable('constants-mapping-materials');

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isFetching, isError } = useMaterialMappings({
    page: table.page,
    limit: table.limit,
    ...(table.queryParams.sort !== undefined
      ? { sort: table.queryParams.sort }
      : {}),
    ...(table.queryParams.filter !== undefined
      ? { filter: table.queryParams.filter }
      : {}),
  });
  const { data: dropdownData } = useMaterialsDropdown();
  const patchMutation = usePatchMaterialMapping();
  const deleteMutation = useDeleteMaterialMappings();
  const createMutation = useCreateMaterialMappings();

  if (isError)
    return (
      <EmptyState
        title={t('error.title')}
        description={t('error.description')}
      />
    );

  const materialsDropdown = dropdownData?.data ?? [];

  const columns: ColumnDef<IMaterialMapping>[] = [
    {
      id: 'name',
      accessorKey: 'name',
      header: t('material.columns.retracedName'),
      enableSorting: true,
      cell: ({ getValue }) => (
        <TextValue
          value={getValue<string | null>()}
          emptyNode={<span className='text-xs text-muted-foreground'>—</span>}
        />
      ),
    },
    {
      id: 'retracedMaterialId',
      accessorKey: 'retracedMaterialId',
      header: t('columns.retracedId'),
      enableSorting: true,
      cell: ({ getValue }) => (
        <TextValue
          value={getValue<string | null>()}
          emptyNode={<span className='text-xs text-muted-foreground'>—</span>}
        />
      ),
    },
    {
      id: 'externalName',
      accessorKey: 'externalName',
      header: t('material.columns.yourName'),
      enableSorting: true,
      meta: {
        editor: 'text',
        editKey: 'externalName',
      },
      cell: ({ getValue }) => <TextValue value={getValue<string | null>()} />,
    },
    {
      id: 'externalId',
      accessorKey: 'externalId',
      header: t('material.columns.constantId'),
      enableSorting: true,
      meta: {
        editor: 'text',
        editKey: 'externalId',
      },
      cell: ({ getValue }) => <TextValue value={getValue<string | null>()} />,
    },
    {
      id: 'updatedAt',
      accessorKey: 'updatedAt',
      header: t('columns.updatedAt'),
      enableSorting: true,
      cell: ({ row }) => <UpdatedAtValue value={row.original.updatedAt} />,
    },
  ];

  const filterDefs: FilterDef<IMaterialMapping>[] = [
    {
      id: 'retracedMaterialId',
      label: t('material.columns.retracedName'),
      type: 'select',
      options: materialsDropdown.map(m => ({ key: m.id, label: m.name })),
    },
    { id: 'externalName', label: t('material.columns.yourName'), type: 'text' },
    { id: 'externalId', label: t('material.columns.constantId'), type: 'text' },
  ];

  const handleCellCommit = async (
    _rowIndex: number,
    colId: string,
    rawValue: unknown,
    row: IMaterialMapping,
  ) => {
    try {
      await patchMutation.mutateAsync({
        id: row.id,
        body: {
          externalName:
            colId === 'externalName'
              ? String(rawValue ?? '')
              : (row.externalName ?? ''),
          externalId:
            colId === 'externalId'
              ? String(rawValue ?? '')
              : (row.externalId ?? ''),
          retracedMaterialId: row.retracedMaterialId ?? '',
        },
      });
      void queryClient.invalidateQueries({
        queryKey: materialMappingKeys.all(),
      });
    } catch {
      toast.error(t('toast.saveError'));
      throw new Error('patch failed');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync([...selectedIds]);
      void queryClient.invalidateQueries({
        queryKey: materialMappingKeys.all(),
      });
      toast.success(
        t('toast.deleteSuccess', { count: String(selectedIds.size) }),
      );
      setSelectedIds(new Set());
      setDeleteOpen(false);
    } catch {
      toast.error(t('toast.deleteError'));
    }
  };

  const handleCreate = async (
    rows: {
      retracedMaterialId: string;
      externalId: string;
      externalName: string;
    }[],
  ) => {
    try {
      await createMutation.mutateAsync(rows);
      void queryClient.invalidateQueries({
        queryKey: materialMappingKeys.all(),
      });
      toast.success(t('toast.createSuccess'));
      setCreateOpen(false);
    } catch {
      toast.error(t('toast.createError'));
    }
  };

  return (
    <>
      <MappingTable
        columns={columns}
        filterDefs={filterDefs}
        data={data?.data ?? []}
        isFetching={isFetching}
        totalCount={data?.meta?.totalCount ?? 0}
        table={table}
        storageKey='constants-mapping-materials'
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        rowIdKey='id'
        onAdd={() => setCreateOpen(true)}
        onDelete={() => setDeleteOpen(true)}
        isDeleting={deleteMutation.isPending}
        emptyTitle={t('material.empty.title')}
        emptyDescription={t('material.empty.description')}
        onCellCommit={handleCellCommit}
      />
      <DeleteConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onConfirm={handleDelete}
        count={selectedIds.size}
      />
      <MaterialCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        materialsDropdown={materialsDropdown}
        onConfirm={handleCreate}
        isLoading={createMutation.isPending}
      />
    </>
  );
}
